<template>
  <div class="d-select text-dark"
       v-on-clickaway="close"
       :class="{'openFromBottom': openFromBottom}">
    <div class="width-100 no-print"
         :class="{'box-shadow': boxShadow}">
      <div class="d-select__input-look"
           @click="open"
           :class="{'active': showDropdown}">
        <div v-for="selectedUser in usersThatAreSelected"
             class="d-select__selected__item"
             :class="{'readyToDelete': isReadyToDelete(selectedUser)}">
          {{selectedUser.name}}
          <template v-if="userOccEvents(selectedUser) && userOccEvents(selectedUser).length > 0">
            <div class="icon-warning item__icon-warning marg-5-left"
                 :class="{'item_icon__hide': selectedUser.occupied.events.length === 1 && selectedUser.occupied.events[0].event_type === 'presence' || selectedUser.isOnLesson && selectedUser.occupied.events.length === 1}">
            </div>
            <div class="d-select__selected__item__secret-label">
              <div class="d-select__selected__item__secret-label__event"
                   v-for="(event, index) in userOccEvents(selectedUser)" v-if="index < 3">
                {{event.title}}
              </div>
            </div>
          </template>
          <div type="button" v-if="!selectedUser.isErrorUser"
               @click.stop="removeUser(selectedUser)"
               class="d-select__selected__item__remove">
            <span>×</span>
          </div>
        </div>
        <template v-if="isLoading">
          <spinner style="margin: 7px 0 0 0;" color="blue" size="small"></spinner>
        </template>
        <template v-else>
          <input type="search"
                 ref="inputfield"
                 v-model="searchText"
                 :placeholder="placeholderText"
                 class="d-select__header__search"
                 :class="[{'show': showDropdown}, {'input-active': searchText.length > 0 || selectedUsers.length < 1}]"
                 @keypress="selectWithKeyboard('', $event)"
                 @keypress.enter.prevent="selectWithKeyboard('enter')"
                 @keydown.down.prevent="selectWithKeyboard('down')"
                 @keydown.up.prevent="selectWithKeyboard('up')"
                 @keydown.delete="deleteUserWithKeyboard"/>
        </template>
      </div>
      <div class="d-select__items"
           v-if="showDropdown">
        <ul class="d-select__items__container"
            ref="dropdown">
          <template v-if="isUpdatingUsers">
            <li class="item-template flex space-between">
              <spinner style="margin: 4px 0 0 0;" color="blue" size="small"></spinner>
            </li>
          </template>
          <template v-else v-for="(item, index) in searchedUsers">
            <li
              :class="[
              {'isReadyForSelection': isSelectedWithKeyboard(item)},
              {'item_is_selected': isSelected(item)}]"
              :key="item.user_id"
              @mouseenter="setIsReadyForSelection(item, index)"
              @click.stop="toggleUser(item)">
              <template v-if="item.occupied && item.occupied.events.length > 0">
                <div class="item-template isOccupied"
                     :class="[
                     {'item-is-present': item.occupied.events.length === 1 && item.occupied.events[0].event_type === 'presence'},
                     {'item-is-already-attached': item.isOnLesson}]">
                  <span v-if="item.icon" :class="item.icon" class="marg-10-right"></span>
                  <img v-if="item.default_thumb_url" :src="item.default_thumb_url" alt="">
                  <div>{{ item.name }}</div>
                  <!-- If user is occupied -->
                  <div class="warning-signs">
                    <template v-for="event in item.occupied.events">
                      <!-- If user is occupied -->
                      <template v-if="event.event_type !== 'presence'">
                        <div class="marg-10-left" v-if="item.isOnLesson && item.currentEventId === event.event_id">
                          <span class="icon-user"></span> {{ i18n['also_assigned'] }}
                        </div>
                        <div class="marg-10-left warning-signs__sign" v-show="item.currentEventId !== event.event_id">
                          <span class="icon-warning"></span> {{event.title}}
                        </div>
                      </template>
                      <div class="marg-10-left"
                           v-show="item.occupied.events.length === 1 && event.event_type === 'presence'">
                        <span class="icon-clock"></span> {{ i18n['working_hours'] }}: {{event.event_time_start | localeTime}} -
                        {{event.event_time_end | localeTime}}
                      </div>
                    </template>
                  </div>
                </div>
                <!-- END -->
              </template>
              <div class="item-template" v-else>
                <span v-if="item.icon" :class="item.icon" class="marg-10-right"></span>
                <img v-if="item.default_thumb_url" :src="item.default_thumb_url" alt="">
                <div>{{ item.name }}</div>
              </div>
            </li>
          </template>
        </ul>
        <div class="no-match" v-if="searchedUsers.length < 1">
          <slot name="noMatch">
            <h4>{{ $t('vue2.dselect.no_matching_results') }}</h4>
          </slot>
        </div>
      </div>
    </div>
    <transition name="fadeIn">
      <div class="subModal__bg" v-if="subFromIsOnLesson !== null">
        <div class="subModal">
          <h2>{{ i18n['would_you_like_to_set'] }} {{subFromIsOnLesson.name}} {{ i18n['as_a_substitute_instead_of_teacher'] }}?</h2>
          <div class="subModal__buttons">
            <button @click="setSubFromIsOnLesson">{{ i18n['yes'] }}</button>
            <button class="bttn-simple bttn" @click="subFromIsOnLesson = null">{{ i18n['no'] }}</button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
  import spinner from '../common/Spinner'
  import {mixin as clickaway} from 'vue-clickaway'
  import moment from 'moment'
  import docendo from '../../docendo'

  const I18N = {
    da: {
      user_not_found: 'Brugeren er fjernet fra skolen',
      would_you_like_to_set: 'Vil du sætte',
      as_a_substitute_instead_of_teacher: 'på som vikar i stedet for underviser',
      yes: 'Ja',
      no: 'Nej',
      working_hours: 'Arbejdstid',
      also_assigned: 'Også tilknyttet'
    },
    nb: {
      user_not_found: 'Brukeren er fjernet fra skolen',
      would_you_like_to_set: 'Vil du sette',
      as_a_substitute_instead_of_teacher: 'som vikar istedenfor lærer',
      yes: 'Ja',
      no: 'Nej',
      working_hours: 'Arbeidstid',
      also_assigned: 'Også tilknyttet'
    },
    en: {
      user_not_found: 'User is removed from school',
      would_you_like_to_set: 'Would you like to set',
      as_a_substitute_instead_of_teacher: 'as a substitute instead of a teacher',
      yes: 'Yes',
      no: 'No',
      working_hours: 'Working hours',
      also_assigned: 'Also assigned'
    }
  }

  export default {
    mixins: [clickaway],
    components: {spinner},
    props: {
      items: {
        default: () => [],
        type: Array
      },
      itemsSelected: {
        default: () => [],
        type: Array
      },
      isLoading: {
        default: false,
        type: Boolean
      },
      show: {
        default: false,
        type: Boolean
      },
      multiple: {
        default: true,
        type: Boolean
      },
      placeholder: {
        type: String,
        default: ''
      },
      eventId: {
        type: Number,
        required: false
      },
      user: {},
      isUpdatingUsers: {
        type: Boolean,
        default: false
      }
    },
    computed: {
      locale () {
        return this.$store.getters.locale
      },
      i18n () {
        return I18N[this.locale]
      },
      usersThatAreSelected () {
        let subIds = this.selectedUsers.map(user => user.id)
        return subIds.map(subID => this.items.find(user => user.id === subID))
      },
      withoutSelectedItems () {
        return this.items.filter(el => {
          return this.selectedUsers.filter(secEl => {
            return secEl.id === el.id
          }).length === 0
        })
      },
      searchedUsers () {
        return this.withoutSelectedItems.filter(user => {
          return user.name !== '' ? user.name.match(new RegExp(this.searchText, 'i')) : ''
        })
      },
      placeholderText () {
        let placeholder = this.placeholder.length > 0 ? this.placeholder : this.$i18n.t('vue2.dselect.placeholder')
        return this.selectedUsers.length > 0 ? '' : placeholder
      }
    },
    data () {
      return {
        showDropdown: false,
        searchText: '',
        selectedUsers: [],
        keyboardSelected: null,
        keyboardSelectedIndex: 0,
        readyToDelete: null,
        openFromBottom: false,
        subFromIsOnLesson: null
      }
    },
    watch: {
      // Close from from component
      show () {
        if (!this.show) {
          this.close()
        }
      },
      searchedUsers () {
        this.keyboardSelectedIndex = 0
        this.keyboardSelected = this.searchedUsers[0]
      },
      searchText () {
        this.$emit('searchText', this.searchText)
      }
    },
    filters: {
      localeTime (eventTime) {
        return moment(eventTime).format('LT')
      }
    },
    methods: {
      addedIsOnLesson (sub) {
        this.subFromIsOnLesson = sub
      },
      setSubFromIsOnLesson () {
        this.selectedUsers.push(this.items[this.items.indexOf(this.subFromIsOnLesson)])
        docendo.eventAddSubstitute(this.eventId, this.subFromIsOnLesson.id, this.user.id, data => {
          this.subFromIsOnLesson = null
          this.$store.commit('updateEventOnAllEventsInAHash', JSON.parse(data))
        })
      },
      userOccEvents (selectedUser) {
        if (selectedUser.occupied && selectedUser.occupied.events.length > 0) {
          let occEventsWithoutPresenceAndCurrentEvent = selectedUser.occupied.events.filter(lesson => lesson.event_id !== this.eventId && lesson.event_type !== 'presence')
          return occEventsWithoutPresenceAndCurrentEvent
        } else {
          return false
        }
      },
      checkHeight () {
        const bcr = this.$el.getBoundingClientRect()
        const pxFromTop = bcr.top
        const pxFromBottom = window.innerHeight - (bcr.top + bcr.height)
        if (pxFromBottom < 330 && pxFromTop > 330) {
          this.openFromBottom = true
        }
      },
      isReadyToDelete (user) {
        if (!this.readyToDelete) {
          return
        }
        return this.readyToDelete.id === user.id
      },
      scrollTokeyboardSelected () {
        setTimeout(() => {
          let user = document.querySelector('.isReadyForSelection')
          let selectedOffsetTop = user.offsetTop
          let selectedHeight = user.getBoundingClientRect().height
          let totalHeight = selectedOffsetTop + selectedHeight

          let top = this.$refs.dropdown.scrollTop
          let height = this.$refs.dropdown.getBoundingClientRect().height
          let bottom = top + height

          if (totalHeight > bottom) {
            this.$refs.dropdown.scrollTop = this.$refs.dropdown.scrollTop + selectedHeight
          }

          if (selectedOffsetTop < top) {
            this.$refs.dropdown.scrollTop = this.$refs.dropdown.scrollTop - selectedHeight
          }
        }, 0)
      },
      setIsReadyForSelection (user, idx) {
        this.keyboardSelected = null
        this.keyboardSelected = user
        this.keyboardSelectedIndex = idx
      },
      selectWithKeyboard (stroke, e) {
        // Open Dropdown if closed and typing
        if (!this.showDropdown) {
          this.open()
        }
        if (stroke === 'down') {
          if ((this.keyboardSelectedIndex + 1) >= this.searchedUsers.length) {
            return
          }
          this.keyboardSelectedIndex = this.keyboardSelectedIndex + 1
          this.keyboardSelected = this.searchedUsers[this.keyboardSelectedIndex]
          this.scrollTokeyboardSelected()
        }
        if (stroke === 'up') {
          if ((this.keyboardSelectedIndex - 1) < 0) {
            return
          }
          this.keyboardSelectedIndex = this.keyboardSelectedIndex - 1
          this.keyboardSelected = this.searchedUsers[this.keyboardSelectedIndex]
          this.scrollTokeyboardSelected()
        }
        if (stroke === 'enter') {
          if (this.keyboardSelected === null) {
            return
          }
          if (this.searchedUsers.length < 1) {
            this.$emit('enter')
            return
          }
          this.toggleUser(this.keyboardSelected)
        }
      },
      isSelectedWithKeyboard (user) {
        return this.keyboardSelected && this.keyboardSelected.id === user.id
      },
      deleteUserWithKeyboard () {
        if (this.searchText.length > 0 || this.selectedUsers.length < 1) {
          return
        }
        if (this.readyToDelete === null) {
          this.readyToDelete = this.selectedUsers[this.selectedUsers.length - 1]
        } else {
          this.removeUser(this.selectedUsers[this.selectedUsers.length - 1])
          this.readyToDelete = null
        }
      },
      close () {
        this.$emit('close')
        if (!this.showDropdown) {
          return
        }
        this.showDropdown = false
        this.keyboardSelected = null
        this.keyboardSelectedIndex = 0
        this.readyToDelete = null
        this.searchText = ''
        this.$emit('show', false)
        this.openFromBottom = false
      },
      open () {
        this.checkHeight()
        this.showDropdown = true
        this.$emit('show', true)
        this.setFocus()
        this.keyboardSelectedIndex = 0
      },
      toggleUser (item) {
        if (item.tag) {
          this.selectAllWithTag(item.name)
          return
        }
        if (this.isSelected(item)) {
          this.removeUser(item)
        } else {
          if (item.isOnLesson) {
            this.addedIsOnLesson(item)
            this.close()
            return
          }
          if (this.multiple) {
            this.selectedUsers.push(item)
            this.$emit('add', item)
          } else {
            this.selectedUsers = []
            this.selectedUsers.push(item)
            this.$emit('add', item)
          }
          this.$emit('update', this.selectedUsers)
        }
        this.close()
      },
      selectAllWithTag (tag) {
        let usersWithTag = this.items.filter(item => {
          return item.user_tags && item.user_tags.indexOf(tag) > -1
        })
        usersWithTag.forEach(user => {
          let idx = this.items.findIndex(x => x.id === user.id)
          if (!this.isSelected(this.items[idx])) {
            this.selectedUsers.push(this.items[idx])
          }
        })
        this.close()
        this.$emit('update', this.selectedUsers)
      },
      removeUser (item) {
        this.selectedUsers.splice(this.selectedUsers.indexOf(item), 1)
        this.$emit('remove', item)
        this.$emit('update', this.selectedUsers)
        this.close()
      },
      isSelected (user) {
        return this.selectedUsers.indexOf(user) > -1
      },
      setFocus () {
        this.$refs.inputfield.focus()
      }
    },
    mounted () {
      if (this.show) {
        this.open()
      }
      this.selectedUsers = this.itemsSelected
    }
  }
</script>


<style scoped>
  @import '../../assets/css/variables.css';

  .d-select {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    min-height: 30px;
  }

  .d-select__header__search {
    width: 100%;
    transition: width 0.25s;
    border-radius: 4px;
  }

  .d-select__input-look {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    transition: width 0.25s;
    border-radius: 4px;
    background: white;
    cursor: text;
    border: 1px solid #acb6be;
  }

  .d-select__input-look.active {
    border: 1px solid var(--blue);
    border-radius: 4px 4px 0 0;
  }

  .d-select.openFromBottom .d-select__input-look.active {
    border-radius: 0 0 4px 4px;
  }

  .d-select__input-look input[type="search"] {
    border: none !important;
    width: 20px;
  }

  .d-select__input-look input[type="search"].input-active {
    width: 200px;
  }

  .d-select__selected__item {
    display: flex;
    position: relative;
    align-items: center;
    box-sizing: border-box;
    width: max-content;
    color: #333;
    border: 1px solid #c8d6e2;
    background: #f6f9fc;
    border-radius: 4px;
    height: 26px;
    margin: 4px 1px 4px 3px;
    padding: 1px .25em;
    float: left;
    line-height: 24px;
  }

  .d-select__selected__item.readyToDelete {
    background-color: var(--danger);
  }

  .d-select__selected__item__remove {
    float: none;
    margin-right: 0;
    font-size: 20px;
    margin-left: 5px;
    appearance: none;
    padding: 0;
    cursor: pointer;
    background: 0 0;
    border: 0;
    font-weight: 700;
    color: #000;
    text-shadow: 0 1px 0 #fff;
    opacity: .2;
  }

  .d-select__selected__item__remove:hover {
    opacity: 1;
  }

  .d-select__items {
    position: absolute;
    z-index: 10000;
    background-color: #ffffff;
    padding: 0;
    width: 100%;
    top: 100%;
    left: 0;
    right: 0;
    border: 1px solid var(--blue);
    border-width: 0 1px 1px 1px;
    border-radius: 0 0 4px 4px;
    box-shadow: 0 4px 14px 0 rgba(0, 0, 0, .2), 0 0 0 1px rgba(0, 0, 0, .05);
  }

  .d-select.openFromBottom .d-select__items {
    top: auto;
    bottom: 100%;
    border-width: 1px 1px 0px 1px;
    border-radius: 4px 4px 0 0;
  }

  .d-select__items__container {
    max-height: 50vh;
    overflow-y: auto;
    margin: 0;
  }

  .d-select__items h4 {
    padding: 5px 15px;
  }

  .d-select__items li.isReadyForSelection {
    background-color: var(--blue);
    color: white;
  }

  .item_is_selected {
    background-color: #cccccc;
  }

  .item-template {
    display: flex;
    align-items: center;
    border-left: 2px solid transparent;
    padding: 5px 15px;
    cursor: pointer;
  }

  .item-template > img {
    height: 18px;
    margin-right: 10px;
  }

  .item-template.isOccupied {
    background-color: rgba(251, 127, 152, 0.5);
    border-left: 2px solid #fb7f98;
    color: black;
  }

  .item-template.isOccupied.item-is-present {
    background-color: rgba(153, 197, 244, 0.5);
    border-left: 2px solid #99c5f4;
    color: black;
  }

  .item-template.isOccupied.item-is-already-attached {
    background-color: rgba(136, 209, 170, 0.5);
    border-left: 2px solid #4CAF50;
    color: black;
  }

  .no-match {
    background: #f4f4f4;
    border-radius: 0 0 4px 4px;
  }

  .warning-signs {
    display: flex;
    align-items: center;
  }

  warning-signs__sign {
    margin-right: 10px;
    display: flex;
  }

  .warning-sign > span {
    margin-right: 5px;
  }

  .item__icon-warning {
    display: block;
    padding: 2px 4px;
    font-size: 10px;
    background-color: rgba(251, 127, 152, 0.5);
    border-left: 2px solid #fb7f98;
    color: black;
  }

  .item_icon__hide {
    display: none !important;
  }

  .d-select__selected__item__secret-label {
    display: none !important;
    position: absolute;
    z-index: 100;
    align-items: center;
    bottom: calc(100%);
    left: calc(100% - 39px);
    font-size: 10px;
    padding: 0 6px 0 4px;
    box-shadow: 4px 4px 8px rgba(0, 0, 0, .3);
    background-color: #fdc4cf;
    border-left: 2px solid #fb7f98;
    color: black;
    white-space: nowrap;
  }

  .item__icon-warning:hover + .d-select__selected__item__secret-label {
    display: flex !important;
    transform: translateY(2px);
    transition: all 1s;
  }

  .d-select__selected__item__secret-label__event {
    display: flex;
    margin-left: 2px;
  }

  .d-select__selected__item__secret-label__event:after {
    content: ',';
  }

  .d-select__selected__item__secret-label__event:last-child:after {
    content: '';
  }


  .subModal__bg {
    position: fixed;
    background-color: rgba(0, 0, 0, 0.51);
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 100;
  }

  .subModal {
    z-index: 10001;
    width: 500px;
    height: 200px;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background-color: white;
    box-shadow: 0 24px 38px 3px rgba(0, 0, 0, .14), 0 9px 46px 8px rgba(0, 0, 0, .12), 0 11px 15px -7px rgba(0, 0, 0, .2);
    border-radius: 4px;
    border: 1px solid #ccc;
  }

  .subModal h2 {
    background: #f6f7f8;
    text-align: center;
    padding: 10px 20px;
    border-bottom: 1px solid #d8d8d8;
  }

  .subModal__buttons {
    display: flex;
    padding-top: 40px;
    margin: 0 auto;
    justify-content: center;
  }

  .subModal__buttons > button {
    width: 100px;
  }

  .subModal__buttons > button:first-child {
    margin-right: 10px;
  }

  /* Enter and leave animations can use different */
  /* durations and timing functions.              */
  .fadeIn-enter-active {
    transition: all .3s ease;
  }

  .fadeIn-leave-active {
    transition: all .2s cubic-bezier(1.0, 0.5, 0.8, 1.0);
  }

  .fadeIn-enter, .fadeIn-leave-to
    /* .slide-fade-leave-active below version 2.1.8 */
  {
    transform: translateX(10px);
    opacity: 0;
  }

</style>



// WEBPACK FOOTER //
// DSelectSubsSub.vue?66ddcdb0